import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Space, Table, Tag } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { ObjectCardSection } from '../../components/layout/ObjectCard';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  loadWarehouseCorrections,
  WarehouseCorrectionSummary,
} from './warehouseCorrectionApi';

export default function WarehouseCorrectionPanel({
  documentId,
  documentType,
  editing,
  version,
  reasonMinLength,
}: {
  documentId: string;
  documentType: 'PZ' | 'WZ';
  editing: boolean;
  version?: string;
  /** Mirrors the backend minimum (WZ: wzCorrectionService.WZ_CORRECTION_REASON_MIN_LENGTH). */
  reasonMinLength?: number;
}) {
  const { t } = useTranslation();
  const namespace = documentType.toLowerCase();
  const [history, setHistory] = useState<WarehouseCorrectionSummary[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    loadWarehouseCorrections(documentType, documentId)
      .then((rows) => {
        if (active) {
          setHistory(rows);
          setFailed(false);
        }
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [documentId, documentType, version]);
  if (!editing && !failed && history.length === 0) return null;
  // One card section (DESIGN.md §3 A): the correction reason while editing,
  // then the correction history.
  return (
    <ObjectCardSection
      wide
      testId="warehouse-correction-section"
      tone="orange"
      icon={<HistoryOutlined />}
      title={t(editing ? `${namespace}:correction.title` : `${namespace}:correction.history`)}
    >
      {editing && (
        <>
          <p className="obj-section__hint" title={t(`${namespace}:correction.description`)}>{t(`${namespace}:correction.description`)}</p>
          <Form.Item
            name="correctionReason"
            label={t(`${namespace}:correction.reason`)}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t(`${namespace}:correction.reasonRequired`),
              },
              ...(reasonMinLength
                ? [{
                  validator: (_: unknown, value: string) => (
                    !value || value.trim().length >= reasonMinLength
                      ? Promise.resolve()
                      : Promise.reject(new Error(t(`${namespace}:correction.reasonMinLength`, { min: reasonMinLength })))
                  ),
                }]
                : []),
            ]}
          >
            <Input.TextArea maxLength={1000} showCount rows={2} />
          </Form.Item>
        </>
      )}
      {failed && (
        <Alert
          type="warning"
          message={t(`${namespace}:correction.historyFailed`)}
        />
      )}
      {history.length > 0 && (
        <Table
          rowKey="id"
          size="small"
          dataSource={history}
          title={editing ? () => t(`${namespace}:correction.history`) : undefined}
          pagination={{ pageSize: 5 }}
          columns={[
            {
              title: t(`${namespace}:correction.date`),
              dataIndex: 'createdAt',
              render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
            },
            {
              title: t(`${namespace}:correction.author`),
              dataIndex: 'createdByName',
            },
            {
              title: t(`${namespace}:correction.reason`),
              dataIndex: 'reason',
              render: (reason: string, record: WarehouseCorrectionSummary) => (
                <Space size={6}>
                  {record.kind === 'STORNO' && (
                    <Tag className="warehouse-storno-tag">{t('pz:storno.tag')}</Tag>
                  )}
                  <span>{reason}</span>
                </Space>
              ),
            },
          ]}
        />
      )}
    </ObjectCardSection>
  );
}
